import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { YOUTU_BRAND_AVATAR_URL, YOUTU_BRAND_NAME } from '../lib/youtu-brand';
import {
  createWechatPermanentEntryIssue,
  createWechatPermanentEntryViewerSession,
  getPublicWechatPermanentEntry,
  getWechatPermanentEntryIssue,
  type PublicWechatPermanentEntry,
  type WechatPermanentEntryIssue,
} from '../lib/wechat-permanent-entry-api';
import {
  applyShareCardMetadata,
  configureWechatShareCard,
  type AgentShareCard,
} from '../lib/agent-profile-share';
import { HUANG_RUQUN_WELCOME_MESSAGE } from '../lib/manling-finance-profile-copy';
import './NiuboshiExpertSharePage.css';

type IssueStage = 'idle' | 'loading' | 'pending' | 'confirmed' | 'expired' | 'revoked' | 'error';

type ExpertShareConfig = {
  slug: string;
  publicToken: string;
  displayName: string;
  avatarPath: string;
  backgroundPath: string;
  topLogoPath?: string;
  topLogoAlt?: string;
  welcomeText: string;
};

type NiuboshiExpertSharePageProps = {
  configOverride?: ExpertShareConfig | null;
};

const HOUMAO_ORIGIN = 'https://houmao.com';
const DEFAULT_EXPERT_SLUG = 'niuboshi';
const PLACEHOLDER_AVATAR_PATH = '/images/expert-avatar-placeholder.svg';
const POLL_INTERVAL_MS = 2000;
const QR_READY_POLL_INTERVAL_MS = 350;
const AUTO_REFRESH_DELAY_MS = 650;
const MAX_LOCAL_EXPIRY_TIMEOUT_MS = 2_147_483_647;

const EXPORTED_EXPERT_SHARE_PROFILES = [
  ["ad-creative-strategist", "\u5468\u8d8a", "/images/agents/ad-creative-strategist.png", "\u5e7f\u544a\u521b\u610f\u7b56\u7565\u5e08"],
  ["ai-image-prompt-engineer", "\u6c88\u6f88", "/images/agents/ai-image-prompt-engineer.png", "AI\u56fe\u50cf\u63d0\u793a\u8bcd\u5de5\u7a0b\u5e08"],
  ["book-co-creator", "\u8bb8\u6e05\u598d", "/images/agents/book-co-creator.png", "\u56fe\u4e66\u8054\u5408\u521b\u4f5c\u8005"],
  ["china-ecommerce-operations-expert", "\u848b\u627f\u6cfd", "/images/agents/china-ecommerce-operations-expert.png", "\u4e2d\u56fd\u7535\u5546\u8fd0\u8425\u4e13\u5bb6"],
  ["corporate-training-designer", "\u97e9\u745e\u5b81", "/images/agents/corporate-training-designer.png", "\u4f01\u4e1a\u57f9\u8bad\u8bbe\u8ba1\u5e08"],
  ["cultural-intelligence-strategist", "\u6234\u660e\u73ca", "/images/agents/cultural-intelligence-strategist.png", "\u6587\u5316\u667a\u80fd\u7b56\u7565\u5e08"],
  ["discovery-coach", "\u5434\u6b63\u8a00", "/images/agents/discovery-coach.png", "\u9500\u552e\u9700\u6c42\u53d1\u73b0\u65b9\u6cd5\u8bba\u4e13\u5bb6"],
  ["document-generation-expert", "\u6c88\u4fee\u6587", "/images/agents/document-generation-expert.png", "\u6587\u6863\u751f\u6210\u4e13\u5bb6"],
  ["executive-summary-generator", "\u8d75\u660e\u8fdc", "/images/agents/executive-summary-generator.png", "\u6218\u7565\u62a5\u544a\u987e\u95ee"],
  ["fortune-consultant", "\u6797\u5c0f\u6ee1", "/images/agents/fortune-consultant.png", "\u8d5b\u535a\u795e\u7b97\u5b50"],
  ["metamorphosis-practitioner", "\u66f9\u5b88\u5c71", "/images/agents/metamorphosis-practitioner.png", "\u521b\u4e1a\u5fc3\u6001\u966a\u4f34\u8005"],
  ["narrative-designer", "\u79e6\u5ddd", "/images/agents/narrative-designer.png", "\u53d9\u4e8b\u8bbe\u8ba1\u5e08"],
  ["product-management-expert", "\u7a0b\u7267\u91ce", "/images/agents/product-management-expert.png", "\u4ea7\u54c1\u7ba1\u7406\u4e13\u5bb6"],
  ["proposal-strategist", "\u53f6\u8212\u5b81", "/images/agents/proposal-strategist.png", "\u65b9\u6848\u7b56\u5212\u5e08"],
  ["rapid-prototyping-engineer", "\u9648\u5609\u5f0b", "/images/agents/rapid-prototyping-engineer.png", "\u5feb\u901f\u539f\u578b\u5de5\u7a0b\u5e08"],
  ["recruitment-expert", "\u5468\u4e3d\u840d", "/images/agents/recruitment_expert.png", "\u62db\u8058\u8fd0\u8425\u4e0e\u4eba\u624d\u83b7\u53d6\u4e13\u5bb6"],
  ["sales-coach", "\u9ad8\u632f\u5b87", "/images/agents/sales-coach.png", "\u9500\u552e\u6559\u7ec3"],
  ["test-results-analyst", "\u90b5\u6587\u658c", "/images/agents/test-results-analyst.png", "\u6d4b\u8bd5\u5206\u6790\u4e13\u5bb6"],
  ["trend-researcher", "\u4f55\u5ddd", "/images/agents/trend-researcher.png", "\u884c\u4e1a\u8d8b\u52bf\u4e13\u5bb6"],
  ["viral-topic-master", "\u4e54\u53ef\u7136", "/images/agents/viral-topic-master.png", "\u7206\u6b3e\u9009\u9898\u7b56\u5212\u4e13\u5bb6"],
  ["wechat-official-account-expert", "\u5510\u82e5\u6960", "/images/agents/wechat-official-account-expert.png", "\u5fae\u4fe1\u516c\u4f17\u53f7\u8fd0\u8425\u4e13\u5bb6"],
] as const satisfies readonly (readonly [string, string, string, string])[];

const RELEASE_EXPERT_SHARE_PROFILES = [
  ['aihot', 'AI情报员', '/images/agents/aihot.png'],
  ['baidu_seo_expert', '王晓宇', '/images/agents/baidu_seo_expert.png'],
  ['content_creator_zhang_xiaokai', '张小凯', '/images/agents/content_creator_zhang_xiaokai.png'],
  ['cross_border_ecommerce_expert', '浩川', '/images/agents/cross_border_ecommerce_expert.png'],
  ['douyin_strategist', '王大才', '/images/agents/douyin_strategist.png'],
  ['kuaishou_strategist', '老葛', '/images/agents/kuaishou_strategist.png'],
  ['livestream_ecommerce_coach', '冉十娘', '/images/agents/livestream_ecommerce_coach.png'],
  ['long_manuscript_expert', '刘青云', '/images/agents/long_manuscript_expert.png'],
  ['recruitment_expert', '莫红', '/images/agents/recruitment_expert.png'],
  ['tik_tok_strategist', '苗大侠', '/images/agents/tik_tok_strategist.png'],
  ['viral_topic_master', '张志文', '/images/agents/viral_topic_master.png'],
  ['wechat_official_account_expert', '王家卫', '/images/agents/wechat_official_account_expert.png'],
  ['xiaohongshu_operations_expert', '吴小红', '/images/agents/xiaohongshu_operations_expert.png'],
] as const satisfies readonly (readonly [string, string, string])[];

function buildExportedExpertWelcomeText(displayName: string, profession: string): string {
  return `\u4f60\u597d\uff0c\u6211\u662f${displayName}\u3002\u8fdb\u5165\u804a\u5929\u540e\u5148\u53d1\u4e00\u53e5\u201c\u4f60\u597d\u201d\uff0c\u6211\u4f1a\u7ed3\u5408${profession}\u5e2e\u4f60\u62c6\u89e3\u95ee\u9898\u3002`;
}

function buildReleaseExpertWelcomeText(displayName: string): string {
  return `我是${displayName}，进来后先发“你好”，我会帮你快速拆解问题。`;
}

const EXPORTED_EXPERT_SHARE_CONFIGS: Record<string, ExpertShareConfig> = Object.fromEntries(
  EXPORTED_EXPERT_SHARE_PROFILES.map(([slug, displayName, avatarPath, profession]) => [
    slug,
    {
      slug,
      publicToken: `expert-${slug}`,
      displayName,
      avatarPath,
      backgroundPath: avatarPath,
      welcomeText: buildExportedExpertWelcomeText(displayName, profession),
    },
  ]),
);

const RELEASE_EXPERT_SHARE_CONFIGS: Record<string, ExpertShareConfig> = Object.fromEntries(
  RELEASE_EXPERT_SHARE_PROFILES.map(([slug, displayName, avatarPath]) => [
    slug,
    {
      slug,
      publicToken: `expert-${slug.replace(/_/g, '-')}`,
      displayName,
      avatarPath,
      backgroundPath: avatarPath,
      welcomeText: buildReleaseExpertWelcomeText(displayName),
    },
  ]),
);

const EXPERT_SHARE_CONFIGS: Record<string, ExpertShareConfig> = {
  ...EXPORTED_EXPERT_SHARE_CONFIGS,
  ...RELEASE_EXPERT_SHARE_CONFIGS,
  niuboshi: {
    slug: 'niuboshi',
    publicToken: 'expert-avoman',
    displayName: '\u725b\u535a\u58eb',
    avatarPath: '/images/agents/avoman.png',
    backgroundPath: '/images/share/niuboshi-expert-bg.png',
    welcomeText:
      '\u4f60\u597d\uff0c\u6211\u662f\u725b\u535a\u58eb\u3002\u8fdb\u5165\u804a\u5929\u540e\u5148\u53d1\u4e00\u53e5\u201c\u4f60\u597d\u201d\uff0c\u6211\u5c31\u53ef\u4ee5\u5f00\u59cb\u5e2e\u4f60\u770b\u79cd\u690d\u3001\u751f\u6001\u548c\u7ecf\u8425\u95ee\u9898\u3002',
  },
  'niuboshi-menglian': {
    slug: 'niuboshi-menglian',
    publicToken: 'expert-avoman-menglian',
    displayName: '\u725b\u535a\u58eb-\u5b5f\u8fde\u79d1\u7814\u4e2d\u5fc3',
    avatarPath: '/images/agents/menglian-research-mark.png',
    backgroundPath: '/images/share/niuboshi-expert-bg.png',
    topLogoPath: '/images/agents/menglian-research-logo.png',
    topLogoAlt: '\u5b5f\u8fde\u53bf\u725b\u6cb9\u679c\u79d1\u5b66\u6280\u672f\u7814\u7a76\u4e2d\u5fc3\u6807\u8bc6',
    welcomeText:
      '\u4f60\u597d\uff0c\u6211\u662f\u725b\u535a\u58eb-\u5b5f\u8fde\u79d1\u7814\u4e2d\u5fc3\u3002\u8fdb\u5165\u804a\u5929\u540e\u5148\u53d1\u4e00\u53e5\u201c\u4f60\u597d\u201d\uff0c\u6211\u4f1a\u7ed3\u5408\u5b5f\u8fde\u8d44\u6599\u548c\u5929\u6c14\u65e5\u62a5\u5e2e\u4f60\u770b\u725b\u6cb9\u679c\u73b0\u573a\u95ee\u9898\u3002',
  },
  luyou: {
    slug: 'luyou',
    publicToken: 'expert-luyou',
    displayName: YOUTU_BRAND_NAME,
    avatarPath: YOUTU_BRAND_AVATAR_URL,
    backgroundPath: YOUTU_BRAND_AVATAR_URL,
    welcomeText:
      `你好，我是${YOUTU_BRAND_NAME}。进入聊天后先发一句“你好”，我会帮你把私域经营拆成可执行的增长方案。`,
  },
  'luyou-store': {
    slug: 'luyou-store',
    publicToken: 'expert-luyou-store',
    displayName: YOUTU_BRAND_NAME,
    avatarPath: YOUTU_BRAND_AVATAR_URL,
    backgroundPath: YOUTU_BRAND_AVATAR_URL,
    welcomeText:
      `你好，我是${YOUTU_BRAND_NAME}。进入聊天后先发一句“你好”，我会帮你把会员规则、到店加微、顾客标签、回访、带训和复盘整理成可执行的门店经营动作。`,
  },
  'activity-bangbang': {
    slug: 'activity-bangbang',
    publicToken: 'activity-bangbang-assistant',
    displayName: '\u6d3b\u52a8\u5e2e\u5e2e',
    avatarPath: '/images/agents/activity_bangbang_assistant.jpg',
    backgroundPath: '/images/agents/activity_bangbang_assistant.jpg',
    welcomeText:
      '\u4f60\u597d\uff0c\u6211\u662f\u6d3b\u52a8\u5e2e\u5e2e\u3002\u8fdb\u5165\u804a\u5929\u540e\u5148\u53d1\u4e00\u53e5\u201c\u4f60\u597d\u201d\uff0c\u6211\u4f1a\u5728\u6743\u9650\u548c\u786e\u8ba4\u8fb9\u754c\u5185\uff0c\u5e2e\u4f60\u7ba1\u7406\u7ec4\u7ec7\u3001\u6d3b\u52a8\u3001\u5ba1\u6279\u3001\u62a5\u540d\u3001\u901a\u77e5\u548c\u63d0\u9192\u3002',
  },
  'feiyi-ecommerce-xufeng': {
    slug: 'feiyi-ecommerce-xufeng',
    publicToken: 'expert-feiyi-ecommerce-xufeng',
    displayName: '\u975e\u9057\u7535\u5546\u4e13\u5bb6\u5f90\u5cf0',
    avatarPath: '/images/agents/feiyi-ecommerce-xufeng.jpg',
    backgroundPath: '/images/agents/feiyi-ecommerce-xufeng.jpg',
    welcomeText:
      '\u4f60\u597d\uff0c\u6211\u662f\u975e\u9057\u7535\u5546\u4e13\u5bb6\u5f90\u5cf0\u3002\u8fdb\u5165\u804a\u5929\u540e\u5148\u53d1\u4e00\u53e5\u201c\u4f60\u597d\u201d\uff0c\u6211\u4f1a\u7ed3\u5408\u975e\u9057\u4e0e\u8001\u5b57\u53f7\u7535\u5546\u5b9e\u6218\uff0c\u5e2e\u4f60\u62c6\u89e3\u4ea7\u54c1\u3001\u5185\u5bb9\u3001\u6e20\u9053\u3001\u8f6c\u5316\u548c\u590d\u8d2d\u95ee\u9898\u3002',
  },
  manling: {
    slug: 'manling',
    publicToken: 'expert-manling',
    displayName: '\u9ec4\u5982\u7fa4\u8d22\u52a1\u4e13\u5bb6',
    avatarPath: '/images/agents/manling_finance.jpg',
    backgroundPath: '/images/agents/manling_finance.jpg',
    welcomeText: HUANG_RUQUN_WELCOME_MESSAGE,
  },
  'manling-finance-mentor': {
    slug: 'manling-finance-mentor',
    publicToken: 'expert-manling-finance-mentor',
    displayName: '\u66fc\u7075\u8d22\u52a1\u5e26\u6559\u8001\u5e08',
    avatarPath: '/images/agents/manling_finance.jpg',
    backgroundPath: '/images/agents/manling_finance.jpg',
    welcomeText:
      '\u4f60\u597d\uff0c\u6211\u662f\u66fc\u7075\u8d22\u52a1\u5e26\u6559\u8001\u5e08\u3002\u8fdb\u5165\u804a\u5929\u540e\u5148\u53d1\u4e00\u53e5\u201c\u4f60\u597d\u201d\uff0c\u6211\u4f1a\u5e26\u7740\u4f60\u62c6\u89e3\u804c\u4e1a\u8def\u5f84\u3001\u9762\u8bd5\u6c9f\u901a\u3001\u56e2\u961f\u534f\u4f5c\u4e0e\u4e13\u4e1a\u80fd\u529b\u6210\u957f\u95ee\u9898\u3002',
  },
  'yunnan-guge': {
    slug: 'yunnan-guge',
    publicToken: 'expert-yunnan-guge',
    displayName: '云南谷哥(Good 品牌）',
    avatarPath: '/images/agents/yunnan-guge.jpg',
    backgroundPath: '/images/agents/yunnan-guge.jpg',
    welcomeText:
      '我是谷哥，关于品牌商业的一切，不清楚就问我，从核心定位到执行路径，聊透、做扎实，让每个经手的品牌都带着“好”的底色生长。有什么问题要问我吗？',
  },
  fahuoniu: {
    slug: 'fahuoniu',
    publicToken: 'expert-fahuoniu',
    displayName: '\u7269\u6d41\u4e13\u5bb6\u5c0f\u738b',
    avatarPath: '/images/agents/fahuoniu_expert.png',
    backgroundPath: '/images/agents/fahuoniu_expert.png',
    welcomeText:
      '\u4f60\u597d\uff0c\u6211\u662f\u7269\u6d41\u4e13\u5bb6\u5c0f\u738b\u3002\u8fdb\u5165\u804a\u5929\u540e\u5148\u53d1\u4e00\u53e5\u201c\u4f60\u597d\u201d\uff0c\u6211\u53ef\u4ee5\u5e2e\u4f60\u5206\u6790\u53d1\u8d27\u3001\u5c65\u7ea6\u548c\u6210\u672c\u95ee\u9898\u3002',
  },
  zhangdaoshi: {
    slug: 'zhangdaoshi',
    publicToken: '27f646d6a7e243b4bf3b49d597cec6b1',
    displayName: '\u5f20\u9053\u58eb',
    avatarPath: '/images/agents/bazi_expert.png',
    backgroundPath: '/images/agents/bazi_expert.png',
    welcomeText:
      '\u4f60\u597d\uff0c\u6211\u662f\u5f20\u9053\u58eb\u3002\u8fdb\u5165\u804a\u5929\u540e\u5148\u53d1\u4e00\u53e5\u201c\u4f60\u597d\u201d\uff0c\u6211\u53ef\u4ee5\u5e2e\u4f60\u770b\u8282\u594f\u3001\u5173\u7cfb\u548c\u51b3\u7b56\u65f6\u70b9\u3002',
  },
  duanyongping: {
    slug: 'duanyongping',
    publicToken: 'expert-duan-yongping',
    displayName: '\u6bb5\u6c38\u5e73',
    avatarPath: '/images/agents/universal_duanyongping.png',
    backgroundPath: '/images/agents/universal_duanyongping.png',
    welcomeText:
      '\u4f60\u597d\uff0c\u6211\u662f\u6bb5\u6c38\u5e73\u5206\u8eab\u3002\u8fdb\u5165\u804a\u5929\u540e\u5148\u53d1\u4e00\u53e5\u201c\u4f60\u597d\u201d\uff0c\u6211\u53ef\u4ee5\u5e2e\u4f60\u7528\u5546\u4e1a\u5e38\u8bc6\u62c6\u89e3\u95ee\u9898\u3002',
  },
  ninggaoning: {
    slug: 'ninggaoning',
    publicToken: 'expert-ning-gaoning',
    displayName: '\u5b81\u9ad8\u5b81',
    avatarPath: '/images/agents/universal_ninggaoning.png',
    backgroundPath: '/images/agents/universal_ninggaoning.png',
    welcomeText:
      '\u4f60\u597d\uff0c\u6211\u662f\u5b81\u9ad8\u5b81\u5206\u8eab\u3002\u8fdb\u5165\u804a\u5929\u540e\u5148\u53d1\u4e00\u53e5\u201c\u4f60\u597d\u201d\uff0c\u6211\u53ef\u4ee5\u5e2e\u4f60\u770b\u4ea7\u4e1a\u3001\u7ec4\u7ec7\u548c\u957f\u671f\u7ade\u4e89\u529b\u3002',
  },
  'andrej-karpathy': {
    slug: 'andrej-karpathy',
    publicToken: 'expert-andrej-karpathy',
    displayName: 'Andrej Karpathy',
    avatarPath: '/images/agents/universal_andrej_karpathy.png',
    backgroundPath: '/images/agents/universal_andrej_karpathy.png',
    welcomeText:
      'Hi, I am Andrej Karpathy. Send "hello" after entering chat, and I can help reason through AI systems and engineering decisions.',
  },
  'elon-musk': {
    slug: 'elon-musk',
    publicToken: 'expert-elon-musk',
    displayName: 'Elon Musk',
    avatarPath: '/images/agents/universal_elon_musk.png',
    backgroundPath: '/images/agents/universal_elon_musk.png',
    welcomeText:
      'Hi, I am Elon Musk. Send "hello" after entering chat, and I can help break problems down from first principles.',
  },
  'richard-feynman': {
    slug: 'richard-feynman',
    publicToken: 'expert-richard-feynman',
    displayName: 'Richard Feynman',
    avatarPath: '/images/agents/universal_richard_feynman.png',
    backgroundPath: '/images/agents/universal_richard_feynman.png',
    welcomeText:
      'Hi, I am Richard Feynman. Send "hello" after entering chat, and I can help make complex ideas simple and testable.',
  },
  'ilya-sutskever': {
    slug: 'ilya-sutskever',
    publicToken: 'expert-ilya-sutskever',
    displayName: 'Ilya Sutskever',
    avatarPath: '/images/agents/universal_ilya_sutskever.png',
    backgroundPath: '/images/agents/universal_ilya_sutskever.png',
    welcomeText:
      'Hi, I am Ilya Sutskever. Send "hello" after entering chat, and I can help think through AI capability and safety questions.',
  },
  mrbeast: {
    slug: 'mrbeast',
    publicToken: 'expert-mrbeast',
    displayName: 'MrBeast',
    avatarPath: '/images/agents/universal_mrbeast.png',
    backgroundPath: '/images/agents/universal_mrbeast.png',
    welcomeText:
      'Hi, I am MrBeast. Send "hello" after entering chat, and I can help improve ideas, hooks, and retention.',
  },
  'charlie-munger': {
    slug: 'charlie-munger',
    publicToken: 'expert-charlie-munger',
    displayName: 'Charlie Munger',
    avatarPath: '/images/agents/universal_charlie_munger.png',
    backgroundPath: '/images/agents/universal_charlie_munger.png',
    welcomeText:
      'Hi, I am Charlie Munger. Send "hello" after entering chat, and I can help inspect decisions with mental models and inversion.',
  },
  'naval-ravikant': {
    slug: 'naval-ravikant',
    publicToken: 'expert-naval-ravikant',
    displayName: 'Naval Ravikant',
    avatarPath: '/images/agents/universal_naval_ravikant.png',
    backgroundPath: '/images/agents/universal_naval_ravikant.png',
    welcomeText:
      'Hi, I am Naval Ravikant. Send "hello" after entering chat, and I can help think through leverage, compounding, and freedom.',
  },
  'paul-graham': {
    slug: 'paul-graham',
    publicToken: 'expert-paul-graham',
    displayName: 'Paul Graham',
    avatarPath: '/images/agents/universal_paul_graham.png',
    backgroundPath: '/images/agents/universal_paul_graham.png',
    welcomeText:
      'Hi, I am Paul Graham. Send "hello" after entering chat, and I can help sharpen startup, product, and writing decisions.',
  },
  'steve-jobs': {
    slug: 'steve-jobs',
    publicToken: 'expert-steve-jobs',
    displayName: 'Steve Jobs',
    avatarPath: '/images/agents/universal_steve_jobs.png',
    backgroundPath: '/images/agents/universal_steve_jobs.png',
    welcomeText:
      'Hi, I am Steve Jobs. Send "hello" after entering chat, and I can help focus product, taste, and end-to-end experience.',
  },
  'justin-sun': {
    slug: 'justin-sun',
    publicToken: 'expert-justin-sun',
    displayName: 'Justin Sun',
    avatarPath: '/images/agents/universal_justin_sun.png',
    backgroundPath: '/images/agents/universal_justin_sun.png',
    welcomeText:
      'Hi, I am Justin Sun. Send "hello" after entering chat, and I can help think through attention, narrative, and growth.',
  },
  'nassim-taleb': {
    slug: 'nassim-taleb',
    publicToken: 'expert-nassim-taleb',
    displayName: 'Nassim Taleb',
    avatarPath: '/images/agents/universal_nassim_taleb.png',
    backgroundPath: '/images/agents/universal_nassim_taleb.png',
    welcomeText:
      'Hi, I am Nassim Taleb. Send "hello" after entering chat, and I can help inspect tail risk, robustness, and antifragility.',
  },
  'donald-trump': {
    slug: 'donald-trump',
    publicToken: 'expert-donald-trump',
    displayName: 'Donald Trump',
    avatarPath: '/images/agents/universal_donald_trump.png',
    backgroundPath: '/images/agents/universal_donald_trump.png',
    welcomeText:
      'Hi, I am Donald Trump. Send "hello" after entering chat, and I can help think through negotiation, framing, and public messaging.',
  },
  'x-mastery-mentor': {
    slug: 'x-mastery-mentor',
    publicToken: 'expert-x-mastery-mentor',
    displayName: 'X Mastery Mentor',
    avatarPath: PLACEHOLDER_AVATAR_PATH,
    backgroundPath: PLACEHOLDER_AVATAR_PATH,
    welcomeText:
      'Hi, I am X Mastery Mentor. Send "hello" after entering chat, and I can help improve topic selection, writing, and conversion.',
  },
  'zhang-yiming': {
    slug: 'zhang-yiming',
    publicToken: 'expert-zhang-yiming',
    displayName: 'Zhang Yiming',
    avatarPath: '/images/agents/universal_zhang_yiming.png',
    backgroundPath: '/images/agents/universal_zhang_yiming.png',
    welcomeText:
      'Hi, I am Zhang Yiming. Send "hello" after entering chat, and I can help think through product, organization, and high standards.',
  },
  'zhang-xuefeng': {
    slug: 'zhang-xuefeng',
    publicToken: 'expert-zhang-xuefeng',
    displayName: 'Zhang Xuefeng',
    avatarPath: '/images/agents/universal_zhang_xuefeng.png',
    backgroundPath: '/images/agents/universal_zhang_xuefeng.png',
    welcomeText:
      'Hi, I am Zhang Xuefeng. Send "hello" after entering chat, and I can help with education choices and career-path tradeoffs.',
  },
};

function storageKey(publicToken: string): string {
  return `wechat-permanent-entry:${publicToken}:viewer-token`;
}

function buildQrImageUrl(scanLink: string): string {
  const params = new URLSearchParams({
    size: '540',
    data: scanLink,
  });
  return `/api/v1/public/wechat/qr-image?${params.toString()}`;
}

function coerceIssueStage(issue: WechatPermanentEntryIssue): IssueStage {
  if (issue.status === 'creating') {
    return 'loading';
  }
  if (issue.status === 'error') {
    return 'expired';
  }
  if (issue.status !== 'pending') {
    return issue.status;
  }
  if (!issue.scanLink) {
    return 'loading';
  }

  const expiresAtMs = Date.parse(issue.expiresAt);
  if (Number.isFinite(expiresAtMs) && expiresAtMs <= Date.now()) {
    return 'expired';
  }
  return 'pending';
}

function mergeIssueStatus(
  issue: WechatPermanentEntryIssue,
  status: Awaited<ReturnType<typeof getWechatPermanentEntryIssue>>,
): WechatPermanentEntryIssue {
  const qrcode = status.qrcode ?? issue.qrcode;
  const scanLink = status.scanLink ?? issue.scanLink;
  const nextStatus = status.status === 'creating' && scanLink ? 'pending' : status.status;

  return {
    ...issue,
    qrcode,
    scanLink,
    status: nextStatus,
    expiresAt: status.expiresAt,
    attemptId: status.attemptId ?? issue.attemptId,
    invitationId: status.invitationId ?? issue.invitationId,
  };
}

function normalizeExpertSlug(slug: string | undefined): string {
  return (slug ?? DEFAULT_EXPERT_SLUG).trim().toLowerCase() || DEFAULT_EXPERT_SLUG;
}

function buildExpertSharePageUrl(slug: string): string {
  return `${HOUMAO_ORIGIN}/wechat/expert-share/${encodeURIComponent(slug)}`;
}

export function getExpertShareConfigByPublicToken(publicToken: string | undefined): ExpertShareConfig | null {
  const normalizedToken = publicToken?.trim();
  if (!normalizedToken) {
    return null;
  }

  return Object.values(EXPERT_SHARE_CONFIGS).find((config) => config.publicToken === normalizedToken) ?? null;
}

async function restoreViewerToken(publicToken: string): Promise<string> {
  const existingToken = sessionStorage.getItem(storageKey(publicToken));

  try {
    const session = await createWechatPermanentEntryViewerSession(publicToken, existingToken);
    sessionStorage.setItem(storageKey(publicToken), session.viewerToken);
    return session.viewerToken;
  } catch (error) {
    if (!existingToken) {
      throw error;
    }

    sessionStorage.removeItem(storageKey(publicToken));
    const session = await createWechatPermanentEntryViewerSession(publicToken);
    sessionStorage.setItem(storageKey(publicToken), session.viewerToken);
    return session.viewerToken;
  }
}

function buildExpertShareCard(config: ExpertShareConfig): AgentShareCard {
  const pageUrl = buildExpertSharePageUrl(config.slug);
  const imageUrl = new URL(config.backgroundPath, pageUrl).toString();
  return {
    title: `${config.displayName}\u5fae\u4fe1\u8fde\u63a5`,
    description: `\u957f\u6309\u8bc6\u522b\u4e8c\u7ef4\u7801\u8fde\u63a5${config.displayName}\uff0c\u8fdb\u5165\u804a\u5929\u540e\u8bb0\u5f97\u5148\u53d1\u4e00\u53e5\u4f60\u597d\u3002`,
    imageUrl,
    linkUrl: pageUrl,
  };
}

export default function NiuboshiExpertSharePage({
  configOverride = null,
}: NiuboshiExpertSharePageProps = {}) {
  const { expertSlug } = useParams<{ expertSlug?: string }>();
  const normalizedSlug = useMemo(() => normalizeExpertSlug(expertSlug), [expertSlug]);
  const routeConfig = EXPERT_SHARE_CONFIGS[normalizedSlug] ?? null;
  const config = configOverride ?? routeConfig;
  const [entry, setEntry] = useState<PublicWechatPermanentEntry | null>(null);
  const [viewerToken, setViewerToken] = useState<string | null>(null);
  const [issue, setIssue] = useState<WechatPermanentEntryIssue | null>(null);
  const [stage, setStage] = useState<IssueStage>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isIssuing, setIsIssuing] = useState(false);
  const issueRef = useRef<WechatPermanentEntryIssue | null>(null);

  const displayName = config?.displayName ?? '\u4e13\u5bb6\u5206\u8eab';
  const avatarPath = config?.avatarPath || entry?.avatarUrl?.trim() || PLACEHOLDER_AVATAR_PATH;
  const backgroundPath = config?.backgroundPath || avatarPath;
  const isAvatarBackground = backgroundPath === avatarPath;
  const activeIssueId = issue?.issueId ?? null;
  const issueHasScanLink = Boolean(issue?.scanLink);

  useEffect(() => {
    issueRef.current = issue;
  }, [issue]);

  const issueQr = useCallback(async (token: string) => {
    if (!config) {
      return;
    }

    setIsIssuing(true);
    setErrorMessage(null);
    setStage((current) => (current === 'idle' ? 'loading' : current));

    try {
      const createdIssue = await createWechatPermanentEntryIssue(config.publicToken, token);
      setIssue(createdIssue);
      setStage(coerceIssueStage(createdIssue));
    } catch (error) {
      console.warn('expert share issue creation failed', config.slug, error);
      setStage('expired');
      setErrorMessage(null);
    } finally {
      setIsIssuing(false);
    }
  }, [config]);

  useEffect(() => {
    if (!config) return;

    const card = buildExpertShareCard(config);
    applyShareCardMetadata(card);
    void configureWechatShareCard(card);
  }, [config]);

  useEffect(() => {
    let ignore = false;

    async function bootstrap(): Promise<void> {
      setIssue(null);
      setViewerToken(null);
      setStage('loading');
      setErrorMessage(null);

      if (!config) {
        setStage('error');
        setErrorMessage('\u8fd9\u4e2a\u4e13\u5bb6\u5fae\u4fe1\u5165\u53e3\u6682\u65f6\u4e0d\u53ef\u7528\u3002');
        return;
      }

      try {
        const loadedEntry = await getPublicWechatPermanentEntry(config.publicToken);
        if (ignore) return;

        if (loadedEntry.status !== 'active' || !loadedEntry.canIssue) {
          setEntry(loadedEntry);
          setStage('error');
          setErrorMessage(`${config.displayName}\u5fae\u4fe1\u5165\u53e3\u6682\u65f6\u4e0d\u53ef\u7528\u3002`);
          return;
        }

        const token = await restoreViewerToken(loadedEntry.publicToken);
        if (ignore) return;

        setEntry(loadedEntry);
        setViewerToken(token);
        await issueQr(token);
      } catch (error) {
        if (ignore) return;
        console.warn('expert share bootstrap failed', config.slug, error);
        setStage('error');
        setErrorMessage(`${config.displayName}\u5fae\u4fe1\u5165\u53e3\u52a0\u8f7d\u5931\u8d25\uff0c\u8bf7\u5237\u65b0\u91cd\u8bd5\u3002`);
      }
    }

    void bootstrap();
    return () => {
      ignore = true;
    };
  }, [config, issueQr]);

  useEffect(() => {
    if (!issue || stage !== 'pending') {
      return;
    }

    const expiresAtMs = Date.parse(issue.expiresAt);
    if (!Number.isFinite(expiresAtMs)) {
      return;
    }

    const remainingMs = expiresAtMs - Date.now();
    if (remainingMs <= 0) {
      setStage('expired');
      return;
    }
    if (remainingMs > MAX_LOCAL_EXPIRY_TIMEOUT_MS) {
      return;
    }

    const timer = window.setTimeout(() => {
      setStage('expired');
    }, remainingMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [issue, stage]);

  useEffect(() => {
    if (!activeIssueId || (stage !== 'pending' && stage !== 'loading')) {
      return;
    }

    let ignore = false;
    const issueId = activeIssueId;
    const pollIssueStatus = () => {
      void (async () => {
        try {
          const status = await getWechatPermanentEntryIssue(issueId);
          if (ignore || status.issueId !== issueId) return;
          const currentIssue = issueRef.current;
          if (!currentIssue || currentIssue.issueId !== issueId) return;
          const mergedIssue = mergeIssueStatus(currentIssue, status);
          issueRef.current = mergedIssue;
          setIssue(mergedIssue);
          setStage(coerceIssueStage(mergedIssue));
          if (mergedIssue.status === 'error') {
            setErrorMessage(null);
          }
        } catch (error) {
          if (ignore) return;
          console.warn('expert share status poll failed', error);
        }
      })();
    };
    const isWaitingForQr = !issueHasScanLink;
    if (isWaitingForQr) {
      pollIssueStatus();
    }
    const timer = window.setInterval(
      pollIssueStatus,
      isWaitingForQr ? QR_READY_POLL_INTERVAL_MS : POLL_INTERVAL_MS,
    );

    return () => {
      ignore = true;
      window.clearInterval(timer);
    };
  }, [activeIssueId, issueHasScanLink, stage]);

  useEffect(() => {
    if (stage !== 'expired' || !viewerToken || isIssuing) {
      return;
    }

    const timer = window.setTimeout(() => {
      void issueQr(viewerToken);
    }, AUTO_REFRESH_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isIssuing, issueQr, stage, viewerToken]);

  const showQr = Boolean(issue?.scanLink) && stage !== 'loading' && stage !== 'error';
  const statusText =
    stage === 'confirmed'
      ? '\u5df2\u8fde\u63a5'
      : stage === 'expired' || stage === 'loading' || isIssuing
        ? '\u6b63\u5728\u5237\u65b0\u4e8c\u7ef4\u7801'
        : '\u5728\u7ebf \u00b7 \u968f\u65f6\u53ef\u804a';

  return (
    <main className="niuboshi-share-page">
      <div className="niuboshi-share-frame">
        <img
          className={[
            'niuboshi-share-bg',
            isAvatarBackground ? 'is-avatar-background' : '',
          ].filter(Boolean).join(' ')}
          src={backgroundPath}
          alt=""
          aria-hidden="true"
          draggable={false}
        />
        <div className="niuboshi-share-soft-mask" aria-hidden />
        <div className="niuboshi-share-gradient" aria-hidden />
        {config?.topLogoPath ? (
          <img
            className="niuboshi-share-top-logo"
            src={config.topLogoPath}
            alt={config.topLogoAlt ?? `${displayName}\u6807\u8bc6`}
            loading="eager"
            decoding="async"
            draggable={false}
          />
        ) : null}

        <section className="niuboshi-share-qr-panel" aria-live="polite">
          {showQr && issue ? (
            <>
              <p className="niuboshi-share-kicker">{'\u5fae\u4fe1\u8fde\u63a5'}</p>
              <div className="niuboshi-share-qr-card">
                <img
                  className="niuboshi-share-qr"
                  src={buildQrImageUrl(issue.scanLink!)}
                  alt={`${displayName}\u5fae\u4fe1\u8fde\u63a5\u4e8c\u7ef4\u7801`}
                  referrerPolicy="no-referrer"
                />
              </div>
              <h1>{'\u957f\u6309\u8bc6\u522b\u8fde\u63a5'}</h1>
              <p className="niuboshi-share-note">
                {'\u8fdb\u5165\u804a\u5929\u540e\u8bb0\u5f97\u4e3b\u52a8\u53d1\u4e00\u53e5\u201c\u4f60\u597d\u201d\u3002'}
              </p>
              <p className="niuboshi-share-refresh-note">
                {'\u4e8c\u7ef4\u7801\u8fc7\u671f\u4f1a\u81ea\u52a8\u5237\u65b0'}
              </p>
            </>
          ) : (
            <>
              <div className="niuboshi-share-dots" aria-hidden>
                <span />
                <span />
                <span />
              </div>
              <h1>{errorMessage ?? '\u6b63\u5728\u751f\u6210\u8fde\u63a5\u4e8c\u7ef4\u7801'}</h1>
              <p className="niuboshi-share-note">
                {errorMessage ? '\u8bf7\u7a0d\u540e\u5237\u65b0\u9875\u9762\u518d\u8bd5\u3002' : '\u7a0d\u7b49\u4e00\u4e0b\uff0c\u9a6c\u4e0a\u5c31\u53ef\u4ee5\u7ee7\u7eed\u3002'}
              </p>
            </>
          )}
        </section>

        <section className="niuboshi-share-identity" aria-label={`${displayName}\u72b6\u6001`}>
          <div className="niuboshi-share-welcome-row">
            <div className="niuboshi-share-avatar-wrap">
              <img
                className="niuboshi-share-avatar"
                src={avatarPath}
                alt={`${displayName}\u5934\u50cf`}
                loading="eager"
                decoding="async"
              />
              <span>AI</span>
            </div>
            <p className="niuboshi-share-bubble">
              {config?.welcomeText ?? '\u8fdb\u5165\u804a\u5929\u540e\u5148\u53d1\u4e00\u53e5\u201c\u4f60\u597d\u201d\uff0c\u4e13\u5bb6\u5206\u8eab\u5c31\u53ef\u4ee5\u5f00\u59cb\u5e2e\u4f60\u3002'}
            </p>
          </div>
          <h2>{displayName}</h2>
          <div className="niuboshi-share-status">
            <span aria-hidden />
            <p>{statusText}</p>
          </div>
        </section>
      </div>
    </main>
  );
}
